import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/react';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';

const STORAGE_KEY_PATIENT = 'homeopathy_patient';

/**
 * Syncs Clerk authenticated user email with patient state.
 * On login, checks the database for existing patient data and pre-fills it.
 */
export function usePatientSync() {
  const { user, isSignedIn } = useUser();
  const { patient, setPatient } = useGlobalContext();
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !user) {
      fetchedRef.current = false;
      return;
    }

    const email = user.primaryEmailAddress?.emailAddress;
    if (!email || fetchedRef.current) return;
    if (patient.email === email && patient.name && patient.consultationToken) return;

    fetchedRef.current = true;

    const syncPatient = async () => {
      try {
        const res = await apiClient.get('/patients/by-email', { params: { email } });
        const data = res.data;

        // If backend returns a skeleton (no name), we just set the email
        if (!data.name) {
          setPatient({ email });
          return;
        }

        const patientData = {
          email: data.email,
          name: data.name,
          age: data.age,
          gender: data.gender,
          consultationToken:
            patient.consultationToken ||
            (typeof data.consultationToken === 'string' ? data.consultationToken : undefined),
        };
        setPatient(patientData);
        // Preserve consultationToken when syncing patient data from backend
        try {
          const raw = localStorage.getItem(STORAGE_KEY_PATIENT);
          const existing = raw ? JSON.parse(raw) : {};
          localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify({ ...existing, ...patientData }));
        } catch { /* ignore */ }
      } catch (err: any) {
        console.error('Error fetching patient:', err);
      }
    };

    syncPatient();
  }, [isSignedIn, user, patient.email, patient.name, patient.consultationToken, setPatient]);
}
